import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import auth from '../../../config/auth';
import { axiosInstance, axiosInstanceV2 } from '../../../config/axios';

import {
  getHealthDeclaration,
  IspsPortType,
  updateHealthDeclaration,
} from '../components/health/slices/healthSlice';

import { DossierSectionStatusOptions } from './constants';
import { removeNullUndefined } from '../../global/utils/cleanEmptyData';
import { areArraysEqual } from '../../global/utils/helpers';

export const FIRST_BERTH_STAY_ID = 1;

export interface State {
  portCall: PortCallState;
}

export interface ShipOwner {
  uid?: string | null;
  name: string;
  address?: string | null;
  city?: string | null;
  countryCode?: string | null;
  postcode?: string | null;
  identificationNumber?: string | null;
  IMOCompanyNumber?: number | null;
}

export interface VesselData {
  imo: string;
  uid: string;
  shipName: string;
  shipType: string;
  grossTonnage: string;
  deadweight: string;
  length: string;
  flag: string;
  callsign: string;
  mmsi: string;
  buildDate: Date | string | null;
  beam: string;
  shipOwner: ShipOwner | null;
  doubleHull?: boolean;
  hullConfiguration?: string;
}

export interface VesselBunkeringDetailsData {
  uid: string | null;
  vesselUid: string | null;
  rateReceptionPerHour: string | null;
  maxPressure: string | null;
  diameterConnection: string | null;
  canReceiveDifferentProductsSimultaneously: boolean;
  canReceiveDifferentGradesSimultaneously: boolean;
  isDeckCraneAvailable: boolean;
  freeBoardOnArrival: string | null;
  lengthBetweenPerpendiculars: string | null;
  bollardsLocationFromStern: string | null;
  bunkerManifold: {
    sizeOfConnectionGradeAndType: string | null;
    situation: {
      manifoldPosition1: string[];
      manifoldPosition2: string[];
      distanceFromStern: number | null;
      heightAboveWaterline: number | null;
      distanceFromPortSide: number | null;
    };
  };
  isVesselFittedWithScrubber: boolean;
  haveCertifiedPersonnelToAccessBarge: boolean;
}

interface BerthStay {
  berthId: number;
  arrivalDraft: number | null;
  departureDraft: number | null;
  purposeOfCall: string | null;
  suppliesFreshWater: boolean;
  suppliesBunkering: boolean;
  suppliesIce: boolean;
  suppliesElectricity: boolean;
  eta: object | null;
  etc: object | null;
  ets: object | null;
  etd: object | null;
}

interface Berth {
  berthStays: BerthStay[];
}

export interface DangerousGoodsStatus {
  isDangerousGoodsInTransit: boolean;
  isLoadingDangerousGoods: boolean;
  isNotCarryingDangerousGoods: boolean;
  isUnloadingDangerousGoods: boolean;
}

export interface DangerousGoodsData {
  isTransportingExplosives?: boolean;
  dangerousGoodsType?: string[];
  dangerousGoodsStatus: DangerousGoodsStatus | null;
  internationalSafetyManagementCertificate?: {
    shipHasInternationalSafetyManagement?: boolean;
    reasonWhyNot?: string;
    expireDate?: string | Date;
    issuedBy?: string;
    certificateNumber?: string;
  };
}

interface DangerousGoods {
  dangerousGoodsData: DangerousGoodsData;
  dangerousGoodsList: DangerousGoods[];
  dangerousGoodsSearchResults: DangerousGoods[];
  ui: {
    dangerousGoodsSearchResultsLoading: boolean;
    showDropdown: boolean;
  };
}

export interface CrewMember {
  IDExpiryDate: string;
  IDIssuingState: string;
  IDNature: string;
  IDNumber: string;
  birthDate: string;
  birthPlace: string;
  disembarkationDatetime: string;
  embarkationDatetime: string;
  europeanVISANumber: string | null;
  gender: string;
  name: string;
  nationality: string;
  otherRank: string | null;
  portOfDisembarkation: string;
  portOfEmbarkation: string;
  rank: string;
  surname: string;
  telephoneNumber: string;
}

interface CrewList {
  crewList: CrewMember[];
  crewQuantity: number;
  crewListDocumentLink?: string | null;
  fileMetadata?: FileMetadata;
}

interface Crew {
  masterData: {
    givenName: string;
    familyName: string;
    nationality: string;
    phoneNumber: string;
  };
  arrival?: CrewList;
  departure?: CrewList;
  app: {
    arrivalCrewChanged: boolean;
    departureCrewChanged: boolean;
  };
}

interface InspectionsData {
  isSubjectedToExpandedInspection: boolean;
  dateLastExpandedInspection: string | null;
  portLastExpandedInspection: string | null;
  conditionOfCargoTanks?: string;
  conditionOfBallastTanks?: string;
  plannedStatutorySurveys?: string;
  dropdownPortLastExpandedInspection?: PortData;
}

export interface PortData {
  uid: string;
  code: string;
  countryCode: string;
  name: string;
  portLocations: { code: string; name: string }[];
  unlocode: string;
}

export interface PortCallData {
  eta: { estimatedTime: string | null } | null;
  etd: { estimatedTime: string | null } | null;
  atdLastPort: {
    actualTime: string | null;
    lastUpdated?: string | null;
  };
  etaNextPort: {
    estimatedTime: string | null;
    lastUpdated?: string | null;
  };
  lastPort: PortData;
  nextPort: PortData;
  purposeOfCall: string;
  arrivalDraft: string | null;
  departureDraft: string | null;
  isAbleToInternationalShipping: boolean;
  isAuthorizedForShortseaOrCabotage: boolean;
  suppliesFreshWater: boolean;
  suppliesBunkering: boolean;
  suppliesIce: boolean;
  suppliesElectricity: boolean;
}

export interface LastPortcallData {
  portcallUid: string | null;
  unlocode: string | null;
  portName: string | null;
  eta: string | null;
  crewQuantity: number | null;
}

export interface ShipToShipOperation {
  activity: string;
  dateFrom: Date | string | null;
  dateTo: Date | string | null;
  location: string;
  securityMeasure: string;
}

export interface StatusLog {
  createdAt: string;
  portcallUid: string;
  section: string;
  status: DossierSectionStatusOptions;
  uid: string;
  userUid: string;
}

interface PortCall {
  uid: string;
  general: {
    masterName: string;
    masterSurname: string;
    masterPhoneNumber: string;
    masterTreatment: string;
    masterNationality: string;
    masterSignature: string;
    masterSignatureTimestamp: string;
    vesselStamp?: string | null;
    instructionsText: string;
    port: string | null;
    isOpenToMaster: boolean | null;
    unlocode: string;
    isVesselCarryingCargo: boolean;
    agent: {
      uid: string | null;
      name: string | null;
    };
    status: string;
    relations: { ports: PortData[] };
  };
  healthDeclaration: {
    hasValidSanitationCertificateCarriedOnBoard: boolean;
    placeOfIssuance: '';
    issuingDate: string | null;
    expiringDate: string | null;
    reinspectionRequired: boolean;
    hasShipVisitedInfectedArea: boolean;
    infectedAreaPort: string;
    infectedAreaDate: string | null;
    portsVisitedWithinWHO: string[];
    hasAnyPersonDiedOtherwiseThanAccident: boolean;
    howManyDeadPeople: number;
    hasAnyCaseOfInfectiousDisease: boolean;
    hasTheTotalNumberOfIllPassengersGreaterThanNormal: boolean;
    howManyIllPeople: number;
    isThereAnyIllPeopleOnBoard: boolean;
    areYouAwareOfAnyConditionWhichMayLeadToSpreadOfDisease: boolean;
    wasAMedicalPractitionerConsulted: boolean;
    stateParticulars: string[];
    hasSanitaryMeasuresBeenAppliedOnBoard: boolean;
    isThereASickAnimalOnBoard: boolean;
    sanitaryMeasures: string[];
  };
  portCallData: {
    data: PortCallData;
  };
  lastPortCallData: LastPortcallData;
  vesselBunkeringDetailsData: VesselBunkeringDetailsData;
  vesselData: VesselData;
  inspections: {
    data: InspectionsData;
    carriedOutPortSearchResults: string[];
    ui: {
      carriedOutPortLoading: boolean;
    };
  };
  berth: Berth;
  crew: Crew;
  passengers: {
    arrival: PassengersData | null;
    departure: PassengersData | null;
    app: {
      arrivalPassengersChanged: boolean;
      departurePassengersChanged: boolean;
    };
  };
  stowaways: {
    arrivalStowaways: number;
    departureStowaways: number;
  };
  dangerousGoods: DangerousGoods;
  cargo: {
    arrivalCargo: {
      typeOfCargo: string;
      volume: number;
      description: string;
    };
    departureCargo: {
      typeOfCargo: string;
      volume: number;
      description: string;
    };
    declaredCargo: [];
  };
  solas: {
    data: {
      contactDetails24h: string;
      CSOName: string;

      hasValidISSC: boolean;
      certificateExpiryDate: string;
      certificateNumber: string;
      certificateIssuer: string;
      hasApprovedSSP: boolean;
      shipSecurityLevel: string;
      sspNoReason: string;

      wereApprovedProceduresFollowedInShipToShipOperations: boolean;
      securityMeasures: [];
      shipToShipOperations: ShipToShipOperation[];
      hasAdditionalSecurityMeasures: boolean;
      last10PortCalls: IspsPortType[];
    };
    relations: { ports: PortData[] };
  };
  generalDeclaration: object;
  portcallStatusLog: StatusLog[];
  missingFields: boolean;
}

type UpdatePortCallValues = Partial<
  PortCall & {
    arrivalStowaway: number;
    departureStowaway: number;
    masterName: string;
    masterSurname: string;
    masterPhoneNumber: string;
    masterNationality: string;
    isVesselCarryingCargo: boolean;
  }
>;

export type FileMetadata = {
  filename: string;
  filepath: string;
  contentType: string;
  uploadDate?: string | null;
  converted?: boolean;
};

type PassengersData = {
  fileMetadata: FileMetadata | null;
  parsedPassengerQuantity: number | null;
  passengerQuantity: number | null;
  rowsWithIssues: number | null;
};

type UpdatePassengersData = {
  arrivalPassengerQuantity: number | null;
  departurePassengerQuantity: number | null;
};

interface PortCallState {
  portCall: PortCall;
}

const initialState: PortCallState = {
  portCall: {
    uid: '',
    general: {
      masterName: '',
      masterSurname: '',
      masterNationality: '',
      masterPhoneNumber: '',
      masterSignature: '',
      vesselStamp: '',
      masterSignatureTimestamp: '',
      masterTreatment: 'masterTreatment',
      instructionsText: '',
      port: null,
      isOpenToMaster: null,
      unlocode: '',
      isVesselCarryingCargo: false,
      agent: {
        uid: null,
        name: null,
      },
      status: '',
      relations: { ports: [] },
    },
    healthDeclaration: {
      hasValidSanitationCertificateCarriedOnBoard: false,
      placeOfIssuance: '',
      issuingDate: null,
      expiringDate: null,
      reinspectionRequired: false,
      hasShipVisitedInfectedArea: false,
      infectedAreaPort: '',
      infectedAreaDate: null,
      portsVisitedWithinWHO: [],
      hasAnyPersonDiedOtherwiseThanAccident: false,
      howManyDeadPeople: 0,
      hasAnyCaseOfInfectiousDisease: false,
      hasTheTotalNumberOfIllPassengersGreaterThanNormal: false,
      howManyIllPeople: 0,
      isThereAnyIllPeopleOnBoard: false,
      areYouAwareOfAnyConditionWhichMayLeadToSpreadOfDisease: false,
      wasAMedicalPractitionerConsulted: false,
      stateParticulars: [],
      hasSanitaryMeasuresBeenAppliedOnBoard: false,
      isThereASickAnimalOnBoard: false,
      sanitaryMeasures: [],
    },
    portCallData: {
      data: {
        eta: null,
        etd: null,
        atdLastPort: {
          actualTime: '',
          lastUpdated: '',
        },
        etaNextPort: {
          estimatedTime: '',
          lastUpdated: '',
        },
        lastPort: {
          uid: '',
          unlocode: '',
          countryCode: '',
          code: '',
          name: '',
          portLocations: [],
        },
        nextPort: {
          uid: '',
          unlocode: '',
          countryCode: '',
          code: '',
          name: '',
          portLocations: [],
        },
        purposeOfCall: '',
        arrivalDraft: null,
        departureDraft: null,
        isAbleToInternationalShipping: false,
        isAuthorizedForShortseaOrCabotage: false,
        suppliesFreshWater: false,
        suppliesBunkering: false,
        suppliesIce: false,
        suppliesElectricity: false,
      },
    },
    lastPortCallData: {
      portcallUid: null,
      unlocode: null,
      portName: null,
      eta: null,
      crewQuantity: null,
    },
    vesselBunkeringDetailsData: {
      uid: null,
      vesselUid: null,
      rateReceptionPerHour: null,
      maxPressure: null,
      diameterConnection: null,
      canReceiveDifferentProductsSimultaneously: false,
      canReceiveDifferentGradesSimultaneously: false,
      isDeckCraneAvailable: false,
      freeBoardOnArrival: null,
      lengthBetweenPerpendiculars: null,
      bollardsLocationFromStern: null,
      bunkerManifold: {
        sizeOfConnectionGradeAndType: null,
        situation: {
          manifoldPosition1: [],
          manifoldPosition2: [],
          distanceFromStern: null,
          heightAboveWaterline: null,
          distanceFromPortSide: null,
        },
      },
      isVesselFittedWithScrubber: false,
      haveCertifiedPersonnelToAccessBarge: false,
    },
    vesselData: {
      imo: '',
      shipName: '',
      shipType: '',
      grossTonnage: '',
      deadweight: '',
      length: '',
      flag: '',
      callsign: '',
      mmsi: '',
      buildDate: '',
      beam: '',
      uid: '',
      shipOwner: null,
    },
    inspections: {
      data: {
        isSubjectedToExpandedInspection: false,
        portLastExpandedInspection: '',
        dateLastExpandedInspection: '',
      },
      carriedOutPortSearchResults: [],
      ui: {
        carriedOutPortLoading: false,
      },
    },
    berth: {
      berthStays: [
        {
          berthId: FIRST_BERTH_STAY_ID,
          arrivalDraft: null,
          departureDraft: null,
          purposeOfCall: '',
          suppliesFreshWater: false,
          suppliesBunkering: false,
          suppliesIce: false,
          suppliesElectricity: false,
          eta: null,
          etc: null,
          ets: null,
          etd: null,
        },
      ],
    },
    crew: {
      masterData: {
        givenName: '',
        familyName: '',
        nationality: '',
        phoneNumber: '',
      },
      arrival: {
        crewList: [],
        crewQuantity: 0,
        crewListDocumentLink: '',
        fileMetadata: undefined,
      },
      departure: {
        crewList: [],
        crewQuantity: 0,
        crewListDocumentLink: '',
        fileMetadata: undefined,
      },
      app: {
        arrivalCrewChanged: false,
        departureCrewChanged: false,
      },
    },
    passengers: {
      arrival: null,
      departure: null,
      app: {
        arrivalPassengersChanged: false,
        departurePassengersChanged: false,
      },
    },
    stowaways: {
      arrivalStowaways: 0,
      departureStowaways: 0,
    },
    dangerousGoods: {
      dangerousGoodsData: {
        dangerousGoodsType: [],
        dangerousGoodsStatus: null,
        internationalSafetyManagementCertificate: {
          reasonWhyNot: '',
        },
      },
      dangerousGoodsList: [],
      dangerousGoodsSearchResults: [],
      ui: {
        dangerousGoodsSearchResultsLoading: false,
        showDropdown: false,
      },
    },
    cargo: {
      arrivalCargo: {
        typeOfCargo: '',
        volume: 0,
        description: '',
      },
      departureCargo: {
        typeOfCargo: '',
        volume: 0,
        description: '',
      },
      declaredCargo: [],
    },
    solas: {
      data: {
        contactDetails24h: '',
        CSOName: '',

        hasValidISSC: true,
        certificateExpiryDate: '',
        certificateNumber: '',
        certificateIssuer: '',
        hasApprovedSSP: true,
        shipSecurityLevel: 'LEVEL_1',
        sspNoReason: '',

        wereApprovedProceduresFollowedInShipToShipOperations: false,
        securityMeasures: [],
        shipToShipOperations: [],
        hasAdditionalSecurityMeasures: false,
        last10PortCalls: [],
      },
      relations: { ports: [] },
    },
    generalDeclaration: {},
    portcallStatusLog: [],
    missingFields: true,
  },
};

export const getPortCall = createAsyncThunk<
  { data: any; relations: { ports: [] } },
  string
>('portCall/getPortCall', async (uiid, { rejectWithValue }) => {
  const endpoint = `portcalls/${uiid}`;
  const headers = auth.getHeaders();

  try {
    const response = await axiosInstance.get(endpoint, { headers });
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updatePortCall = createAsyncThunk<
  UpdatePortCallValues,
  UpdatePortCallValues,
  { state: State }
>(
  'portCall/updatePortCall',
  async (portCallData, { getState, rejectWithValue }) => {
    const {
      portCall: {
        portCall: { uid },
      },
    } = getState();

    const headers = auth.getHeaders();
    const endpoint = `portcalls/${uid}`;

    try {
      await axiosInstance.put(endpoint, { ...portCallData }, { headers });
      return portCallData;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePortCallStatus = createAsyncThunk<
  string,
  {
    uid: string;
    newStatus: string;
  },
  { state: State }
>('portCall/updatePortCallStatus', async ({ uid, newStatus }, { getState }) => {
  const {
    portCall: {
      portCall: {
        general: { status },
      },
    },
  } = getState();
  try {
    const headers = auth.getHeaders();
    const endpoint = `portcalls/${uid}/status/${newStatus}`;
    await axiosInstance.put(endpoint, {}, { headers });
    return newStatus;
  } catch {
    return status;
  }
});

export const getPortCallData = createAsyncThunk<
  { portcallData: PortCallData },
  string
>('portCall/getPortCallData', async (uid, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  const endpoint = `portcalls/${uid}/portcall-data`;
  try {
    const response = await axiosInstance.get(endpoint, { headers });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getLastPortCallData = createAsyncThunk<
  LastPortcallData,
  { uid: string; list: string }
>(
  'portCall/getLastPortCallData',
  async ({ uid, list }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    const endpoint = `portcalls/${uid}/last?withCrew=true&list=${list}`;
    try {
      const response = await axiosInstance.get(endpoint, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePortCallData = createAsyncThunk<
  Partial<PortCallData>,
  {
    uid: string;
    data: Partial<PortCallData>;
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>(
  'portCall/updatePortCallData',
  async ({ uid, data: portCallPayload }, { getState, rejectWithValue }) => {
    const {
      portCall: {
        portCall: {
          // eslint-disable-next-line no-empty-pattern
          portCallData: {},
        },
      },
    } = getState();
    const headers = auth.getHeaders();
    const endpoint = `portcalls/${uid}/portcall-data`;

    try {
      await axiosInstance.put(
        endpoint,
        {
          portcallData: {
            ...portCallPayload,
          },
        },
        { headers }
      );

      return portCallPayload;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

//what is this?
export const updateMissingFields = createAsyncThunk<
  boolean,
  { isThereMissingFields: boolean }
>('portCall/missingfields', async ({ isThereMissingFields }) => {
  return isThereMissingFields;
});

export const getVesselBunkeringDetails = createAsyncThunk<
  VesselBunkeringDetailsData,
  string,
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/getVesselBunkeringDetails', async (uid, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    const response = await axiosInstance.get(
      `/portcalls/${uid}/vessel-bunkering-details`,
      {
        headers,
      }
    );
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateVesselBunkeringDetails = createAsyncThunk<
  VesselBunkeringDetailsData,
  {
    uid: string;
    data: VesselBunkeringDetailsData;
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>(
  'portCall/updateVesselBunkeringDetails',
  async ({ data, uid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/vessel-bunkering-details`,
        {
          ...data,
        },
        {
          headers,
        }
      );

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getVessel = createAsyncThunk<VesselData, string>(
  'portCall/getVessel',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(`/portcalls/${uid}/vessel`, {
        headers,
      });

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateVessel = createAsyncThunk<
  VesselData,
  {
    uid: string;
    data: VesselData;
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/updateVessel', async ({ data, uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    await axiosInstance.put(
      `/portcalls/${uid}/vessel`,
      {
        ...data,
      },
      {
        headers,
      }
    );

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getInspections = createAsyncThunk<
  {
    data: InspectionsData;
    relations: { ports: PortData[] };
  },
  string
>('portCall/getInspections', async (uid, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    const response = await axiosInstance.get(`/portcalls/${uid}/mou`, {
      headers,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateInspections = createAsyncThunk<
  object,
  {
    uid: string;
    data: object;
  }
>('portCall/updateInspections', async ({ data, uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    const response = await axiosInstance.put(
      `/portcalls/${uid}/mou`,
      {
        ...data,
      },
      {
        headers,
      }
    );

    return response.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getBerth = createAsyncThunk<Berth, string>(
  'portCall/getBerth',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(`/portcalls/${uid}/berth`, {
        headers,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateBerth = createAsyncThunk<
  Berth,
  {
    uid: string;
    data: {
      berthStay: Partial<BerthStay>;
    };
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>(
  'portCall/updateBerth',
  async ({ uid, data }, { getState, rejectWithValue }) => {
    const {
      portCall: {
        portCall: { berth: berthStays },
      },
    } = getState();

    const previousFirstBerthStay = berthStays.berthStays[0];
    const currentFirstBerthStay = {
      ...previousFirstBerthStay,
      ...data.berthStay,
    };
    const otherBerthStays = berthStays.berthStays?.slice(1) || [];
    const newBerthStays = [currentFirstBerthStay, ...otherBerthStays];
    const berthsToApi = {
      berthStays: newBerthStays.map((berth) => {
        const newBerth: BerthStay = {
          ...berth,
          eta: berth?.eta ? removeNullUndefined(berth?.eta) : null,
          etc: berth?.etc ? removeNullUndefined(berth?.etc) : null,
          ets: berth?.ets ? removeNullUndefined(berth?.ets) : null,
          etd: berth?.etd ? removeNullUndefined(berth?.etd) : null,
        };
        return removeNullUndefined(newBerth) as BerthStay;
      }),
    };

    try {
      await axiosInstance.put(`/portcalls/${uid}/berth`, berthsToApi, {
        headers: auth.getHeaders(),
      });

      return berthsToApi;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCrew = createAsyncThunk<
  CrewList,
  { uid: string; listType: 'arrival' | 'departure' },
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/getCrew', async ({ uid, listType }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  const queryParams = `?list=${listType}`;

  try {
    const response = await axiosInstance.get(
      `/portcalls/${uid}/crew${queryParams}`,
      {
        headers,
      }
    );
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getCrewListDocumentLink = createAsyncThunk<
  { crewListDocumentLink: string },
  { uid: string; listType: 'arrival' | 'departure' }
>(
  'portCall/getCrewListDocumentLink',
  async ({ uid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    const queryParams = `?list=${listType}`;
    try {
      const response = await axiosInstance.get(
        `/portcalls/${uid}/document-links/crew-list${queryParams}`,
        {
          headers,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const importCrewList = createAsyncThunk<
  object,
  { uid: string; listType: 'arrival' | 'departure' }
>('portCall/importCrewList', async ({ uid, listType }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  const queryParams = `?list=${listType}`;
  try {
    const response = await axiosInstance.post(
      `/portcalls/${uid}/crew/import${queryParams}`,
      {},
      {
        headers,
      }
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateCrew = createAsyncThunk<
  object,
  {
    uid: string;
    data: Partial<CrewList>;
    listType: 'arrival' | 'departure';
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>(
  'portCall/updateCrew',
  async ({ uid, data, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    const queryParams = `?list=${listType}`;

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/crew${queryParams}`,
        {
          crewList: data?.crewList,
          crewQuantity: data?.crewQuantity,
        },
        {
          headers,
        }
      );

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPassengers = createAsyncThunk<
  {
    data: PassengersData;
    relations: { ports: PortData[] };
  },
  { uid: string; listType: 'arrival' | 'departure' },
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/getPassengers', async ({ uid, listType }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  const queryParams = `?list=${listType}`;

  try {
    const response = await axiosInstance.get(
      `/portcalls/${uid}/passengers${queryParams}`,
      {
        headers,
      }
    );

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updatePassengers = createAsyncThunk<
  Partial<UpdatePassengersData>,
  {
    uid: string;
    data: Partial<UpdatePassengersData>;
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/updatePassengers', async ({ uid, data }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  const queryParams = ``;

  try {
    await axiosInstance.put(
      `/portcalls/${uid}/passengers${queryParams}`,
      data,
      {
        headers,
      }
    );

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const searchPortOfEmbarkation = createAsyncThunk<
  object,
  {
    unlocode: string | null;
    portName: string | null;
  }
>(
  'portCall/searchPortOfEmbarkation',
  async (searchData, { rejectWithValue }) => {
    let endpoint = `/ports`;
    if (searchData.unlocode) {
      endpoint += `?unlocode=${searchData.unlocode}`;
    } else if (searchData.portName) {
      endpoint += `?name=${searchData.portName}`;
    }

    const headers = auth.getHeaders();

    try {
      const response = await axiosInstance.get(endpoint, {
        headers,
      });

      // mapping into format that dropdown expects
      return response.data.data.map(
        (port: { unlocode: string; countryCode: string; name: string }) => {
          return {
            value: port.unlocode,
            unlocode: port.unlocode,
            countryCode: port.countryCode,
            label: port.name + ', ' + port.countryCode,
            name: port.name,
          };
        }
      );
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchPortOfDisembarkation = createAsyncThunk<
  object,
  {
    unlocode: string | null;
    portName: string | null;
  }
>(
  'portCall/searchPortOfDisembarkation',
  async (searchData, { rejectWithValue }) => {
    let endpoint = `/ports`;
    if (searchData.unlocode) {
      endpoint += `?unlocode=${searchData.unlocode}`;
    } else if (searchData.portName) {
      endpoint += `?name=${searchData.portName}`;
    }

    const headers = auth.getHeaders();

    try {
      const response = await axiosInstance.get(endpoint, {
        headers,
      });

      // mapping into format that dropdown expects
      return response.data.data.map(
        (port: { unlocode: string; countryCode: string; name: string }) => {
          return {
            value: port.unlocode,
            countryCode: port.countryCode,
            label: port.name + ', ' + port.countryCode,
          };
        }
      );
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchDangerousGoods = createAsyncThunk<
  DangerousGoods[],
  {
    un: string;
  }
>('portCall/searchDangerousGoods', async ({ un }, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    const response = await axiosInstance.get(`/dangerous-goods?un=${un}`, {
      headers,
    });

    // mapping into format that dropdown expects
    return response.data.data.map(
      (dangerousGood: { name: string; un: string; packingGroup: string }) => {
        return {
          value: dangerousGood.name,
          label: `UN: ${dangerousGood.un}, ${dangerousGood.name}, packingGroup: ${dangerousGood.packingGroup || '-'}`,
          data: dangerousGood,
        };
      }
    );
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getDangerousGoodsData = createAsyncThunk<
  DangerousGoodsData,
  string
>('portCall/getDangerousGoodsData', async (uid, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  try {
    const response = await axiosInstance.get(
      `/portcalls/${uid}/dangerous-goods-data`,
      {
        headers,
      }
    );

    return response.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateDangerousGoodsData = createAsyncThunk<
  DangerousGoodsData,
  {
    uid: string;
    dangerousGoodsData: DangerousGoodsData;
  }
>(
  'portCall/updateDangerousGoodsData',
  async ({ uid, dangerousGoodsData }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/dangerous-goods-data`,
        {
          dangerousGoodsData: {
            ...dangerousGoodsData,
          },
        },
        {
          headers,
        }
      );

      return dangerousGoodsData;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDangerousGoods = createAsyncThunk<DangerousGoods[], string>(
  'portCall/getDangerousGoods',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(
        `/portcalls/${uid}/dangerous-goods`,
        {
          headers,
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateDangerousGoods = createAsyncThunk<
  {
    uid: string;
    dangerousGoods: DangerousGoods[];
  },
  {
    uid: string;
    dangerousGoods: DangerousGoods[];
  },
  {
    state: {
      portCall: PortCallState;
    };
  }
>(
  'portCall/updateDangerousGoods',
  async (dangerousGoodsData, { getState, rejectWithValue }) => {
    const {
      portCall: {
        portCall: { uid },
      },
    } = getState();
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/dangerous-goods`,
        {
          ...dangerousGoodsData,
        },
        {
          headers,
        }
      );

      return dangerousGoodsData;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCargo = createAsyncThunk(
  'portCall/getCargo',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(`/portcalls/${uid}/cargo`, {
        headers,
      });

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateCargo = createAsyncThunk<
  object,
  object,
  {
    state: {
      portCall: PortCallState;
    };
  }
>('portCall/updateCargo', async (cargoData, { getState, rejectWithValue }) => {
  const {
    portCall: {
      portCall: { uid },
    },
  } = getState();
  const headers = auth.getHeaders();

  try {
    await axiosInstance.put(
      `/portcalls/${uid}/cargo`,
      {
        ...cargoData,
      },
      {
        headers,
      }
    );
    return cargoData;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getIsps = createAsyncThunk(
  'portCall/getIsps',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(`/portcalls/${uid}/isps`, {
        headers,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateIsps = createAsyncThunk<
  object,
  {
    uid: string;
    ispsData: object;
  }
>('portCall/updateIsps', async ({ ispsData, uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  try {
    await axiosInstance.put(
      `/portcalls/${uid}/isps`,
      {
        ...ispsData,
      },
      {
        headers,
      }
    );

    return ispsData;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getGeneralDeclaration = createAsyncThunk(
  'portCall/getGeneralDeclaration',
  async (uid, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(
        `/portcalls/${uid}/general-declaration`,
        {
          headers,
        }
      );

      return {
        generalDeclarationAccepted:
          response.data.data.generalDeclarationAccepted,
        generalDeclarationRemarks: response.data.data.generalDeclarationRemarks,
        generalDeclarationRead: response.data.data.generalDeclarationRead,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPortcallStatusLog = createAsyncThunk(
  'portCall/getPortcallStatusLog',
  async (uid: string, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      const response = await axiosInstanceV2.get(
        `/portcalls/portcall-status-log?portcallUid=${uid}`,
        {
          headers,
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createPortcallStatusLog = createAsyncThunk<
  object,
  {
    statusLog: {
      uid: string;
      section: string;
      status: string;
    };
  }
>(
  'portCall/createPortcallStatusLog',
  async ({ statusLog }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstanceV2.post(
        `/portcalls/portcall-status-log`,
        {
          portcallUid: statusLog.uid,
          section: statusLog.section,
          status: statusLog.status,
        },
        {
          headers,
        }
      );

      return statusLog;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateMasterSignature = createAsyncThunk<
  object,
  {
    uid: string;
    image: string;
  }
>(
  'portCall/updateMasterSignature',
  async ({ image, uid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/master-signature`,
        {
          image: image,
        },
        {
          headers,
        }
      );

      return image;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateVesselStamp = createAsyncThunk<
  object,
  {
    uid: string;
    image: string | null;
  }
>('portCall/updateVesselStamp', async ({ image, uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    await axiosInstance.put(
      `/portcalls/${uid}/vessel-stamp`,
      {
        image: image,
      },
      {
        headers,
      }
    );

    return image;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateGeneralDeclaration = createAsyncThunk<
  object,
  {
    uid: string;
    generalDeclaration: {
      generalDeclarationAccepted: boolean;
      generalDeclarationRemarks: boolean;
      generalDeclarationRead: boolean;
    };
  }
>(
  'portCall/updateGeneralDeclaration',
  async ({ generalDeclaration, uid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `/portcalls/${uid}/general-declaration`,
        {
          generalDeclarationAccepted:
            generalDeclaration.generalDeclarationAccepted,
          generalDeclarationRemarks:
            generalDeclaration.generalDeclarationRemarks,
          generalDeclarationRead: generalDeclaration.generalDeclarationRead,
        },
        {
          headers,
        }
      );

      return generalDeclaration;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const portCallSlice = createSlice({
  name: 'portCall',
  initialState,
  reducers: {
    setDepartureCrewChanged: (state, action) => {
      const { departureCrewChanged } = action.payload;
      state.portCall.crew.app.departureCrewChanged = departureCrewChanged;
    },
    setDeparturePassengersChanged: (state, action) => {
      const { departurePassengersChanged } = action.payload;
      state.portCall.passengers.app.departurePassengersChanged =
        departurePassengersChanged;
    },
    showDangerousGoodsModal: (state) => {
      state.portCall.dangerousGoods.ui.showDropdown = true;
    },
    closeDangrousGoodsDropdown: (state) => {
      state.portCall.dangerousGoods.ui.showDropdown = false;
    },
    setHealthDeclaration: (state, action) => {
      const { healthDeclaration } = action.payload;
      state.portCall.healthDeclaration = healthDeclaration;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHealthDeclaration.fulfilled, (state, action) => {
        state.portCall.healthDeclaration = {
          ...state.portCall.healthDeclaration,
          ...action.payload,
        };
      })
      .addCase(updateHealthDeclaration.fulfilled, (state, action) => {
        const healthData = action.payload?.healthData;
        state.portCall.healthDeclaration = {
          ...state.portCall.healthDeclaration,
          ...healthData,
        };
      })
      .addCase(getPortCall.fulfilled, (state, action) => {
        const {
          data: {
            uid,
            status,
            masterName,
            masterSurname,
            masterNationality,
            masterPhoneNumber,
            masterSignature,
            masterSignatureTimestamp,
            masterTreatment,
            arrivalStowaway,
            departureStowaway,
            instructionsText,
            isOpenToMaster,
            unlocode,
            vessel,
            isVesselCarryingCargo,
            agent,
            vesselStamp,
          },
          relations,
        } = action.payload;

        state.portCall = {
          ...state.portCall,
          uid,
          general: {
            ...state.portCall.general,
            status,
            masterName,
            masterSurname,
            masterPhoneNumber,
            masterTreatment,
            masterNationality,
            masterSignature,
            masterSignatureTimestamp,
            instructionsText,
            isOpenToMaster,
            unlocode,
            isVesselCarryingCargo,
            agent,
            vesselStamp,
          },
          solas: {
            ...state.portCall.solas,
            relations,
          },
          vesselData: vessel,
          stowaways: {
            arrivalStowaways: arrivalStowaway,
            departureStowaways: departureStowaway,
          },
        };
      })
      .addCase(updatePortCall.fulfilled, (state, action) => {
        const {
          arrivalStowaway,
          departureStowaway,
          masterName,
          masterSurname,
          masterPhoneNumber,
          masterNationality,
          isVesselCarryingCargo,
        } = action.payload;

        if (masterName) {
          state.portCall.general.masterName = masterName;
        }

        if (masterSurname) {
          state.portCall.general.masterSurname = masterSurname;
        }

        if (masterPhoneNumber) {
          state.portCall.general.masterPhoneNumber = masterPhoneNumber;
        }

        if (masterNationality) {
          state.portCall.general.masterNationality = masterNationality;
        }

        state.portCall.general.isVesselCarryingCargo =
          isVesselCarryingCargo ?? false;

        state.portCall.stowaways.arrivalStowaways = arrivalStowaway
          ? arrivalStowaway
          : state.portCall.stowaways.arrivalStowaways;
        state.portCall.stowaways.departureStowaways = departureStowaway
          ? departureStowaway
          : state.portCall.stowaways.departureStowaways;
      })
      .addCase(updatePortCallStatus.fulfilled, (state, action) => {
        state.portCall = {
          ...state.portCall,
          general: {
            ...state.portCall.general,
            status: action.payload,
          },
        };
      })
      .addCase(getPortCallData.pending, () => {})
      .addCase(getPortCallData.fulfilled, (state, action) => {
        if (action.payload.portcallData) {
          state.portCall.portCallData.data = action.payload.portcallData;
        }
      })
      .addCase(getLastPortCallData.fulfilled, (state, action) => {
        const { portcallUid, unlocode, portName, eta } = action.payload;
        state.portCall.lastPortCallData.portcallUid = portcallUid;
        state.portCall.lastPortCallData.unlocode = unlocode;
        state.portCall.lastPortCallData.portName = portName;
        state.portCall.lastPortCallData.eta = eta;
      })
      .addCase(updatePortCallData.pending, () => {})
      .addCase(updatePortCallData.fulfilled, (state, action) => {
        state.portCall.portCallData.data = {
          ...state.portCall.portCallData.data,
          ...action.payload,
        };
      })
      .addCase(getVessel.fulfilled, (state, action) => {
        state.portCall.vesselData = action.payload;
      })
      .addCase(
        updateVessel.fulfilled,
        (state, action: { payload: VesselData }) => {
          state.portCall.vesselData = action.payload;
        }
      )
      .addCase(getVesselBunkeringDetails.fulfilled, (state, action) => {
        state.portCall.vesselBunkeringDetailsData = action.payload;
      })
      .addCase(updateVesselBunkeringDetails.fulfilled, (state, action) => {
        state.portCall.vesselBunkeringDetailsData = action.payload;
      })
      .addCase(getInspections.fulfilled, (state, action) => {
        const { data, relations } = action.payload;
        state.portCall.inspections.data = data;
        if (data && data.portLastExpandedInspection) {
          const { portLastExpandedInspection } = data;
          relations?.ports?.forEach((port: PortData) => {
            if (port.unlocode === portLastExpandedInspection) {
              data.dropdownPortLastExpandedInspection = port;
            }
          });
        }
      })
      .addCase(updateInspections.fulfilled, () => {})
      .addCase(getBerth.pending, () => {})
      .addCase(getBerth.fulfilled, (state, action) => {
        const { berthStays } = action.payload;
        state.portCall.berth = { berthStays };
      })
      .addCase(updateBerth.pending, () => {})
      .addCase(updateBerth.fulfilled, (state, action) => {
        state.portCall.berth = action.payload;
      })
      .addCase(updateBerth.rejected, () => {})
      .addCase(getCrew.rejected, () => {})
      .addCase(getCrew.pending, () => {})
      .addCase(getCrew.fulfilled, (state, action) => {
        const crew = action.payload;
        const listType = action.meta.arg.listType;

        if (listType === 'arrival') {
          state.portCall.crew.arrival = crew;
        } else if (listType === 'departure') {
          state.portCall.crew.departure = crew;
        }

        if (
          !areArraysEqual(
            state.portCall.crew.arrival,
            state.portCall.crew.departure
          )
        ) {
          state.portCall.crew.app.departureCrewChanged = true;
        }
      })
      .addCase(getCrewListDocumentLink.pending, () => {})
      .addCase(getCrewListDocumentLink.fulfilled, (state, action) => {
        const { crewListDocumentLink } = action.payload;
        const listType = action.meta.arg.listType;
        if (listType === 'arrival') {
          state.portCall.crew.arrival!.crewListDocumentLink =
            crewListDocumentLink;
        } else if (listType === 'departure') {
          state.portCall.crew.departure!.crewListDocumentLink =
            crewListDocumentLink;
        }
      })
      .addCase(updateCrew.pending, () => {})
      .addCase(updateCrew.fulfilled, (state, action) => {
        state.portCall.crew = {
          ...state.portCall.crew,
          ...action.payload,
        };
      })
      .addCase(getPassengers.pending, () => {})
      .addCase(getPassengers.fulfilled, (state, action) => {
        const listType = action.meta.arg.listType;
        const { data } = action.payload;
        state.portCall.passengers[listType] = data;
      })
      .addCase(updatePassengers.pending, () => {})
      .addCase(updatePassengers.fulfilled, (state, action) => {
        state.portCall.passengers = {
          ...state.portCall.passengers,
          ...action.payload,
        };
      })

      .addCase(searchDangerousGoods.pending, (state) => {
        state.portCall.dangerousGoods.dangerousGoodsSearchResults = [];
      })
      .addCase(searchDangerousGoods.fulfilled, (state, action) => {
        state.portCall.dangerousGoods.dangerousGoodsSearchResults =
          action.payload;
        state.portCall.dangerousGoods.ui.showDropdown = true;
      })
      .addCase(getDangerousGoodsData.pending, () => {})
      .addCase(getDangerousGoodsData.fulfilled, (state, action) => {
        state.portCall.dangerousGoods.dangerousGoodsData = action.payload;

        const { dangerousGoodsStatus } = action.payload;
        if (dangerousGoodsStatus) {
          const dangerousGoodsType: string[] = [];
          Object.keys(dangerousGoodsStatus).forEach((key) => {
            if (dangerousGoodsStatus[key as keyof DangerousGoodsStatus]) {
              dangerousGoodsType.push(key);
            }
          });

          state.portCall.dangerousGoods.dangerousGoodsData.dangerousGoodsType =
            dangerousGoodsType;
        }
      })
      .addCase(updateDangerousGoodsData.pending, () => {})
      .addCase(updateDangerousGoodsData.fulfilled, (state, action) => {
        state.portCall.dangerousGoods.dangerousGoodsData = action.payload;

        const { dangerousGoodsStatus } = action.payload;
        if (dangerousGoodsStatus) {
          const dangerousGoodsType: string[] = [];
          Object.keys(dangerousGoodsStatus).forEach((key: string) => {
            if (
              dangerousGoodsStatus[key as keyof typeof dangerousGoodsStatus]
            ) {
              dangerousGoodsType.push(key);
            }
          });

          state.portCall.dangerousGoods.dangerousGoodsData.dangerousGoodsType =
            dangerousGoodsType;
        }
      })
      .addCase(getDangerousGoods.pending, () => {})
      .addCase(getDangerousGoods.fulfilled, (state, action) => {
        state.portCall.dangerousGoods.dangerousGoodsList = action.payload || [];
      })
      .addCase(updateDangerousGoods.pending, () => {})
      .addCase(updateDangerousGoods.fulfilled, (state, action) => {
        state.portCall.dangerousGoods.dangerousGoodsList =
          action.payload.dangerousGoods;
      })
      .addCase(getCargo.pending, () => {})
      .addCase(getCargo.fulfilled, (state, action) => {
        action.payload.declaredCargo = action.payload.declaredCargo || [];

        state.portCall.cargo = action.payload;
      })
      .addCase(updateCargo.pending, () => {})
      .addCase(updateCargo.fulfilled, (state, action) => {
        state.portCall.cargo = {
          ...state.portCall.cargo,
          ...action.payload,
        };
      })
      .addCase(getIsps.pending, () => {})
      .addCase(getIsps.fulfilled, (state, action) => {
        const { data, relations } = action.payload;

        state.portCall.solas.relations = relations || {};
        state.portCall.solas.data = data
          ? {
              ...state.portCall.solas.data,
              ...data,
            }
          : state.portCall.solas.data;

        if (data && data.last10PortCalls) {
          data.last10PortCalls.map(
            (portcall: {
              portName: string;
              portUnlocode: string;
              portNameDropdown: {
                unlocode: string;
                name: string;
                countryCode: string;
              };
              portCountry: string;
            }) => {
              if (portcall.portName && portcall.portUnlocode) {
                portcall.portNameDropdown = {
                  unlocode: portcall.portUnlocode,
                  name: portcall.portName,
                  countryCode: portcall.portCountry,
                };
              }
              return portcall;
            }
          );
        }
      })
      .addCase(updateIsps.pending, () => {})
      .addCase(updateIsps.fulfilled, (state, action) => {
        state.portCall.solas.data = {
          ...state.portCall.solas.data,
          ...action.payload,
        };
      })
      .addCase(getGeneralDeclaration.pending, () => {})
      .addCase(getGeneralDeclaration.fulfilled, (state, action) => {
        state.portCall.generalDeclaration = action.payload;
      })
      .addCase(getPortcallStatusLog.pending, () => {})
      .addCase(getPortcallStatusLog.fulfilled, (state, action) => {
        state.portCall.portcallStatusLog = action.payload;
      })
      .addCase(updateGeneralDeclaration.pending, () => {})
      .addCase(updateGeneralDeclaration.fulfilled, (state, action) => {
        state.portCall.generalDeclaration = action.payload;
      })
      .addCase(updateMissingFields.pending, () => {})
      .addCase(updateMissingFields.fulfilled, (state, action) => {
        state.portCall.missingFields = action.payload;
      });
  },
});

export const {
  setDepartureCrewChanged,
  setDeparturePassengersChanged,
  showDangerousGoodsModal,
  closeDangrousGoodsDropdown,
  setHealthDeclaration,
} = portCallSlice.actions;

export const selectGeneralData = (state: State) =>
  state.portCall.portCall.general;

export const selectPortCallData = (state: State) =>
  state.portCall.portCall.portCallData;

export const selectLastPortCallData = (state: State) =>
  state.portCall.portCall.lastPortCallData;

export const selectVesselBunkeringDetailsData = (state: State) =>
  state.portCall.portCall.vesselBunkeringDetailsData;

export const selectVesselData = (state: State) =>
  state.portCall.portCall.vesselData;

export const selectInspections = (state: State) =>
  state.portCall.portCall.inspections;

export const selectBerth = (state: State) => state.portCall.portCall.berth;

export const selectCrew = (state: State) => state.portCall.portCall.crew;

export const selectPassengers = (state: State) =>
  state.portCall.portCall.passengers;

export const selectStowaways = (state: State) =>
  state.portCall.portCall.stowaways;

export const selectDangerousGoods = (state: State) =>
  state.portCall.portCall.dangerousGoods;

export const selectCargo = (state: State) => state.portCall.portCall.cargo;

export const selectSolas = (state: State) => state.portCall.portCall.solas;

export const selectGeneralDeclaration = (state: State) =>
  state.portCall.portCall.generalDeclaration;

export const selectPortallStatusLog = (state: State) =>
  state.portCall.portCall.portcallStatusLog;

export const selectMissingFields = (state: State) =>
  state.portCall.portCall.missingFields;

export default portCallSlice.reducer;
